<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <!-- 遮罩层 -->
    <div class="fixed inset-0 bg-black bg-opacity-50" @click="handleClose"></div>

    <!-- 弹窗内容 -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <!-- 头部 -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">选择话题</h3>
          <button @click="handleClose" class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- 筛选器 -->
        <div class="p-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex gap-4">
            <select v-model="filterCategoryId" @change="loadTopicsUsage"
              class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500">
              <option :value="undefined">全部分类</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- 话题列表 -->
        <div class="flex-1 overflow-y-auto p-4">
          <div v-if="loading" class="flex justify-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
          <div v-else-if="topicsUsage.length > 0" class="space-y-2">
            <label v-for="topic in topicsUsage" :key="topic.topicId"
              class="flex items-start gap-3 p-3 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              :class="{ 'opacity-50 cursor-not-allowed': isExisting(topic.topicId) }">
              <!-- 复选框 -->
              <input type="checkbox" :value="topic.topicId" v-model="selectedIds" :disabled="isExisting(topic.topicId)"
                class="mt-1 w-4 h-4 text-indigo-600 rounded flex-shrink-0" />

              <!-- 话题信息 -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-gray-900 dark:text-gray-100 font-medium">{{ topic.topicName }}</span>
                  <!-- 已添加标签 -->
                  <span v-if="isExisting(topic.topicId)"
                    class="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">
                    已添加
                  </span>
                </div>

                <!-- 使用状态 -->
                <div v-if="topic.usageCount > 0" class="mt-1 flex flex-wrap gap-1">
                  <span v-for="group in topic.usedByGroups" :key="group.groupId"
                    class="inline-flex items-center px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                    {{ group.groupName }}
                  </span>
                </div>
                <div v-else class="mt-1">
                  <span
                    class="inline-flex items-center px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                    未被使用
                  </span>
                </div>
              </div>
            </label>
          </div>
          <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
            暂无话题
          </div>
        </div>

        <!-- 底部 -->
        <div class="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <span class="text-sm text-gray-500 dark:text-gray-400">
            已选择 {{ selectedIds.length }} 个话题
          </span>
          <div class="flex gap-2">
            <button @click="handleClose"
              class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
              取消
            </button>
            <button @click="handleConfirm" :disabled="selectedIds.length === 0"
              class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              确认添加
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { fetchCategoriesAPI } from '@/api/speaking/topic'
import { getTopicsUsageAPI } from '@/api/speaking/topicGroup'
import type { Category } from '@/types/topic/topic'
import type { TopicUsage } from '@/types/topic/topicGroup'

/**
 * Props
 */
const props = defineProps<{
  /** 已存在的话题ID列表（用于禁用已添加的话题） */
  existingTopicIds: string[]
}>()

/**
 * Emits
 */
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'select', topicIds: string[]): void
}>()

// 状态
const loading = ref(false)
const categories = ref<Category[]>([])
const topicsUsage = ref<TopicUsage[]>([])
const selectedIds = ref<string[]>([])
const filterCategoryId = ref<number | undefined>(undefined)

/**
 * 判断话题是否已存在于当前串题组
 */
const isExisting = (topicId: string): boolean => {
  return props.existingTopicIds.includes(topicId)
}

/**
 * 加载分类列表
 */
const loadCategories = async () => {
  try {
    const response = await fetchCategoriesAPI()
    if (response.data) {
      categories.value = response.data
    }
  } catch (error) {
    console.error('加载分类失败:', error)
  }
}

/**
 * 加载话题使用状态
 */
const loadTopicsUsage = async () => {
  loading.value = true
  try {
    const response = await getTopicsUsageAPI(filterCategoryId.value)
    if (response.data) {
      topicsUsage.value = response.data
    }
  } catch (error) {
    console.error('加载话题使用状态失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 关闭弹窗
 */
const handleClose = () => {
  emit('close')
}

/**
 * 确认选择
 */
const handleConfirm = () => {
  emit('select', selectedIds.value)
}

// 组件挂载时加载数据
onMounted(async () => {
  await loadCategories()
  await loadTopicsUsage()
})
</script>
